import type { ReactNode } from 'react';
import {
  type BlockFenceEditOptions,
  type BlockFenceEntry,
  isBlockFenceKind,
  renderBlockFence,
} from '@/components/block-primitives/block-fence-registry';
import {
  parseCalloutBlockConfig,
  parseChecklistBlockConfig,
  parseClusterBlockConfig,
  parseCompareBlockConfig,
  parseDataViewConfig,
  parseDecisionBlockConfig,
  parseFunnelBlockConfig,
  parseQuoteBlockConfig,
  parseRecordBlockConfig,
  parseStatBlockConfig,
  parseTimelineBlockConfig,
} from './artifact-block-types';
import ArtifactCalloutBlock from './artifact-callout-block';
import ArtifactChecklistBlock from './artifact-checklist-block';
import ArtifactClusterBlock from './artifact-cluster-block';
import ArtifactCompareBlock from './artifact-compare-block';
import ArtifactDataViewBlock from './artifact-data-view-block';
import ArtifactDecisionBlock from './artifact-decision-block';
import ArtifactFunnelBlock from './artifact-funnel-block';
import ArtifactImageComparisonBlock from './artifact-image-comparison-block';
import ArtifactQuoteBlock from './artifact-quote-block';
import ArtifactRecordBlock from './artifact-record-block';
import ArtifactStatBlock from './artifact-stat-block';
import ArtifactTimelineBlock from './artifact-timeline-block';
import { parseImageComparisonBlockConfig } from './image-comparison/image-comparison-contract';

export const artifactBlockRegistry = {
  'image-comparison': {
    parse: parseImageComparisonBlockConfig,
    render: config => <ArtifactImageComparisonBlock config={config} />,
  },
  'data-view': {
    parse: parseDataViewConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactDataViewBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  callout: {
    parse: parseCalloutBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactCalloutBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  timeline: {
    parse: parseTimelineBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactTimelineBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  checklist: {
    parse: parseChecklistBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactChecklistBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  cluster: {
    parse: parseClusterBlockConfig,
    render: config => <ArtifactClusterBlock config={config} />,
  },
  record: {
    parse: parseRecordBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactRecordBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  funnel: {
    parse: parseFunnelBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactFunnelBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  quote: {
    parse: parseQuoteBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactQuoteBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  compare: {
    parse: parseCompareBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactCompareBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  decision: {
    parse: parseDecisionBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactDecisionBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
  stat: {
    parse: parseStatBlockConfig,
    render: (config, { editable, onChange }) => (
      <ArtifactStatBlock config={config} editable={editable} onChange={onChange} />
    ),
  },
} satisfies Record<string, BlockFenceEntry>;

export type ArtifactBlockKind = keyof typeof artifactBlockRegistry;

export const isArtifactBlockKind = (value: string): value is ArtifactBlockKind =>
  isBlockFenceKind(artifactBlockRegistry, value);

export const renderArtifactBlock = (kind: string, raw: string, opts: BlockFenceEditOptions): ReactNode | null =>
  renderBlockFence(artifactBlockRegistry, kind, raw, opts);
